import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ChevronLeft, Download, Loader, Award, ZoomIn, ZoomOut } from "lucide-react";
import { lmsGetCertificateById } from "../api/lmsApi";
import { CertificatePreview } from "./CertificateBuilder";

export default function CertificateView() {
  const { id } = useParams();
  const nav = useNavigate();
  const printRef = useRef();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoomScale, setZoomScale] = useState(null); // null => auto fit

  useEffect(() => {
    lmsGetCertificateById(id)
      .then((res) => setCert(res.data))
      .catch(() => toast.error("Failed to load certificate"))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const isLandscape = cert?.orientation === "landscape";
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Certificate — ${cert?.student_name || ""}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body {
          width: ${isLandscape ? "297mm" : "210mm"};
          height: ${isLandscape ? "210mm" : "297mm"};
          background: #fff; overflow: hidden;
        }
        @page { size: A4 ${isLandscape ? "landscape" : "portrait"}; margin: 0; }
        @media print {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          color-adjust: exact;
        }
        .cert-wrap {
          width: ${isLandscape ? "297mm" : "210mm"};
          height: ${isLandscape ? "210mm" : "297mm"};
          display: flex; align-items: center; justify-content: center;
          page-break-after: avoid; page-break-inside: avoid; overflow: hidden;
        }
        .cert-wrap > * { transform-origin: center center; transform: scale(0.97); }
      </style></head>
      <body><div class="cert-wrap">${content.outerHTML}</div></body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
      <Loader size={32} className="ms2-spin" color="#f59e0b" />
    </div>
  );

  if (!cert) return (
    <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Certificate not found.</div>
  );

  const isLandscape = cert?.orientation === "landscape";
  const W = isLandscape ? 1123 : 794;
  const H = isLandscape ? 794 : 1123;
  const currentScale = zoomScale || (isLandscape ? 0.65 : 0.52);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#f1f5f9", overflow: "hidden" }}>

      {/* Top bar */}
      <div style={{
        background: "#fff",
        borderBottom: "1px solid #e5e7eb",
        padding: "10px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        zIndex: 10,
        flexShrink: 0
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600 }}>
            <ChevronLeft size={16} /> Back
          </button>
          <div style={{ width: 1, height: 20, background: "#e5e7eb" }} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Award size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>{cert.student_name}</div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>{cert.exam_name} · Certificate</div>
          </div>
        </div>

        {/* Center Zoom Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f1f5f9", padding: "3px 6px", borderRadius: 8 }}>
          <button
            title="Zoom Out"
            onClick={() => setZoomScale(Math.max(0.35, +(currentScale - 0.05).toFixed(2)))}
            style={{ border: "none", background: "#fff", borderRadius: 5, padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center", color: "#475569" }}
          >
            <ZoomOut size={13} />
          </button>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#334155", minWidth: 44, textAlign: "center" }}>
            {Math.round(currentScale * 100)}%
          </span>
          <button
            title="Zoom In"
            onClick={() => setZoomScale(Math.min(1.0, +(currentScale + 0.05).toFixed(2)))}
            style={{ border: "none", background: "#fff", borderRadius: 5, padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center", color: "#475569" }}
          >
            <ZoomIn size={13} />
          </button>
          <div style={{ width: 1, height: 14, background: "#cbd5e1", margin: "0 2px" }} />
          <button
            onClick={() => setZoomScale(null)}
            style={{
              border: "none",
              background: zoomScale === null ? "#e0e7ff" : "#fff",
              color: zoomScale === null ? "#3730a3" : "#475569",
              borderRadius: 5,
              padding: "3px 8px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            Fit Screen
          </button>
          <button
            onClick={() => setZoomScale(1.0)}
            style={{
              border: "none",
              background: zoomScale === 1.0 ? "#e0e7ff" : "#fff",
              color: zoomScale === 1.0 ? "#3730a3" : "#475569",
              borderRadius: 5,
              padding: "3px 8px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            100%
          </button>
        </div>

        {/* Print / Download Button */}
        <button onClick={handlePrint} style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "8px 20px", borderRadius: 8, border: "none",
          background: "linear-gradient(135deg,#f59e0b,#d97706)",
          color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          transition: "all 0.15s"
        }}>
          <Download size={14} /> Download / Print
        </button>
      </div>

      {/* Certificate Preview Viewport (Scrollable at 100% / zoom, Centered on Fit) */}
      <div style={{
        flex: 1,
        display: "flex",
        overflow: "auto",
        padding: "24px",
        background: "#f1f5f9"
      }}>
        <div style={{
          width: Math.round(W * currentScale),
          height: Math.round(H * currentScale),
          position: "relative",
          boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
          borderRadius: 8,
          overflow: "hidden",
          flexShrink: 0,
          margin: "auto",
          transition: "width 0.15s ease, height 0.15s ease",
          background: "#ffffff"
        }}>
          <div style={{
            width: W,
            height: H,
            transform: `scale(${currentScale})`,
            transformOrigin: "top left"
          }}>
            <CertificatePreview
              tmpl={cert}
              student={{
                name: cert.student_name,
                parent_name: cert.father_name || cert.guardian_name || (cert.parent_name && !/^\d+$/.test(cert.parent_name) ? cert.parent_name : ""),
                exam_name: cert.exam_name,
                center_name: cert.center_name || cert.school_name || "",
                marks_obtained: cert.marks_obtained,
                total_marks: cert.total_marks,
                grade: cert.grade,
                issued_at: cert.issued_at,
              }}
              printRef={printRef}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
